import { Base } from '@studiometa/js-toolkit';
import { Panel } from '@studiometa/ui';
import { gsap } from 'gsap';
import { Observer } from 'gsap/Observer.js';

gsap.registerPlugin(Observer);

/**
 * @typedef {{
 *   $refs: {
 *     container: HTMLElement,
 *     media: HTMLElement[],
 *   }
 * }} CollagesProps
 */

/**
 * Collages component.
 * Intro animation + infinite scroll with wheel and drag support.
 * @augments {Base<CollagesProps>}
 */
export default class Collages extends Base {
  /**
   * Class config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'Collages',
    refs: ['container', 'media[]'],
    components: { Panel },
  };

  /** @type {number} */
  incrX = 0;

  /** @type {number} */
  incrY = 0;

  /** @type {HTMLElement|null} */
  activeMedia = null;

  /** @type {boolean} */
  isDragging = false;

  /**
   * Play the intro animation, then start the Observer.
   */
  mounted() {
    this.$el.addEventListener('dragstart', (e) => e.preventDefault());
    if (window.__pageTransitioning) {
      document.addEventListener('page:enter', () => this.intro(), { once: true });
    } else if (window.__loaderComplete) {
      this.intro();
    } else {
      document.addEventListener('loader:complete', () => this.intro(), { once: true });
    }
  }

  /**
   * Play the intro animation, then start the Observer.
   */
  intro() {
    const vh = window.innerHeight;

    const originals = this.$refs.media.slice(0, this.$refs.media.length / 4);

    gsap.set(this.$refs.media, { opacity: 1 });

    gsap.from(originals, {
      y: (_, el) => vh - el.getBoundingClientRect().top + el.offsetHeight,
      x: () => gsap.utils.random(-100, 100),
      rotation: () => gsap.utils.random(-15, 15),
      scale: 0.8,
      duration: 0.6,
      ease: 'cubic.out',
      stagger: {
        each: 0.05,
      },
      onComplete: () => this.initObserver(),
    });
  }

  /**
   * Initialize the Observer for infinite scroll.
   */
  initObserver() {
    const { container } = this.$refs;

    const halfX = container.clientWidth / 2;
    const wrapX = gsap.utils.wrap(-halfX, 0);
    this.xTo = gsap.quickTo(container, 'x', {
      duration: 1.5,
      ease: 'power4',
      modifiers: {
        x: gsap.utils.unitize(wrapX),
      },
    });

    const halfY = container.clientHeight / 2;
    const wrapY = gsap.utils.wrap(-halfY, 0);
    this.yTo = gsap.quickTo(container, 'y', {
      duration: 1.5,
      ease: 'power4',
      modifiers: {
        y: gsap.utils.unitize(wrapY),
      },
    });

    this.observer = Observer.create({
      target: this.$el,
      type: 'wheel,touch,pointer',
      onPress: () => {
        this.isDragging = false;
        this.$refs.container.classList.add('!cursor-grabbing');
      },
      onRelease: () => {
        this.$refs.container.classList.remove('!cursor-grabbing');
      },
      onChangeX: (self) => {
        this.isDragging = true;
        if (self.event.type === 'wheel') {
          this.incrX -= self.deltaX;
        } else {
          this.incrX += self.deltaX * 2;
        }
        this.xTo(this.incrX);
      },
      onChangeY: (self) => {
        this.isDragging = true;
        if (self.event.type === 'wheel') {
          this.incrY -= self.deltaY;
        } else {
          this.incrY += self.deltaY * 2;
        }
        this.yTo(this.incrY);
      },
    });
  }

  /**
   * Handle click on a media element.
   * @param {{ event: MouseEvent, index: number, target: HTMLElement }} args
   */
  onMediaClick({ index, target }) {
    if (this.isDragging) return;

    if (this.activeMedia) {
      this.closeZoom();
      return;
    }

    this.activeMedia = target;
    this.observer.disable();

    const rect = target.getBoundingClientRect();
    const padding = 40;
    const vw = window.innerWidth - padding * 2;
    const vh = window.innerHeight - padding * 2;
    const scale = Math.min(vh / rect.height, vw / rect.width);
    const centerX = window.innerWidth / 2 - (rect.left + rect.width / 2);
    const centerY = window.innerHeight / 2 - (rect.top + rect.height / 2);

    gsap.to(target, {
      x: centerX,
      y: centerY,
      scale,
      zIndex: 10,
      duration: 0.6,
      ease: 'power3.out',
    });

    const others = this.$refs.media.filter((_, i) => i !== index);
    gsap.to(others, {
      filter: 'blur(8px)',
      scale: 0.8,
      opacity: 0.3,
      duration: 0.4,
    });

    const header = document.querySelector('.header');
    if (header) {
      gsap.to(header, { filter: 'blur(8px)', opacity: 0.3, pointerEvents: 'none', duration: 0.4 });
    }

    this.onOutsideClick = (e) => {
      if (!this.activeMedia.contains(e.target)) {
        this.closeZoom();
      }
    };
    requestAnimationFrame(() => {
      this.$el.addEventListener('click', this.onOutsideClick);
    });
  }

  /**
   * Close the active zoom.
   */
  closeZoom() {
    if (!this.activeMedia) return;

    gsap.to(this.activeMedia, {
      x: 0,
      y: 0,
      scale: 1,
      zIndex: 0,
      duration: 0.6,
      ease: 'power3.out',
    });

    gsap.to(this.$refs.media, {
      filter: 'blur(0px)',
      scale: 1,
      opacity: 1,
      duration: 0.4,
      onComplete: () => {
        this.$el.removeEventListener('click', this.onOutsideClick);
        this.activeMedia = null;
        this.observer.enable();
      },
    });

    const header = document.querySelector('.header');
    if (header) {
      gsap.to(header, { filter: 'blur(0px)', opacity: 1, pointerEvents: 'auto', duration: 0.4 });
    }
  }

  /**
   * Close zoom on Escape key.
   * @param {object} options
   * @param {boolean} options.ESC
   */
  keyed({ ESC }) {
    if (ESC) {
      this.closeZoom();
    }
  }

  /**
   * Disable Observer when Panel opens.
   */
  onPanelOpen() {
    if (this.observer) {
      this.observer.disable();
    }
  }

  /**
   * Re-enable Observer when Panel closes.
   */
  onPanelClose() {
    if (this.observer) {
      this.observer.enable();
    }
  }

  /**
   * Destroyed hook.
   */
  destroyed() {
    if (this.observer) {
      this.observer.kill();
    }
  }
}
